// src/components/AppTopBar.tsx
"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { UserButton } from "@clerk/nextjs";
import { useRecordContext } from "@/contexts/RecordContext";

export default function AppTopBar() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const recordContext = useRecordContext();

  // 2026-08-04追加→同日訂正：PCのトップバーに/editionsへの入口が無い問題を修正した際、
  // 当初はサイドバーと同じ「有料・トライアル中は非表示」条件で追加した。しかしCEO承認
  // （/editionsのデジタル権利と物理購入状況の分離）により、有料会員にも「物理は未購入」
  // という購入導線を/editionsが表示するようになったため、その前提自体が古くなった。
  // モバイルのハンバーガーメニュー（元から無条件表示）に合わせ、プラン状態に関わらず
  // 常に表示する形に変更。
  const showAddEditionLink = !recordContext?.userProfileLoading && !!recordContext?.userProfile;

  // 2026-09-11・Kazuto指示（TASKS_ACTIVE.md 9/11エントリ「鍵アイコンが表示され開けなく
  // なった件」参照）：course_standardへの手動付与（Shopify購入を経由しないワンオフの
  // 特例アクセス）が本番に恒久的に残る運用になったため、それを使っているアカウントで
  // ログインしている間だけ、うっかり通常の購入者と混同しないよう画面上に常時リマインド
  // を出す。特定UIDへのハードコードは避け、course_standardドキュメントのsourceフィールド
  // （手動付与スクリプトが書き込む"manual_temp_kazuto"）で判定する——将来別アカウントに
  // 同種の一時付与をした場合も自動的に機能する設計。
  // 【9/11追記・Kazuto指摘で表示範囲を限定】全ページ常時表示だと「特別」感が薄れる、
  // また/courses一覧側の🔒表示もこの手動付与の影響を受ける場所なので、/courses配下
  // （一覧・/courses/standard配下とも）に限定して表示する。
  // 2026-11-02：RecordContext が共有している /api/user-courses の結果から判定する。
  const specialAccess = recordContext?.userCourses?.course_standard?.source === "manual_temp_kazuto";

  useEffect(() => {
    if (!open) return;

    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const hideOn = ["/sign-in", "/sign-up"];
  if (hideOn.includes(pathname)) return null;

  return (
    <header className="h-12 flex items-center justify-between px-4 border-b bg-white">
      {/* 左端: ホーム */}
      <Link href="/" aria-label="ホーム" className="flex items-center opacity-70 hover:opacity-100 transition-opacity">
        <Image src="/kataruta-mark.png" alt="KATARUTA" width={28} height={28} className="object-contain" />
      </Link>

      {/* 右端: ナビ */}
      <nav className="flex items-center gap-4">
        <Link href="/rooms" className="rounded px-3 py-1 text-sm font-medium hover:bg-gray-100">
          ルーム
        </Link>
        <Link href="/people" className="hidden md:inline-flex rounded px-3 py-1 text-sm font-medium hover:bg-gray-100">
          ピープル
        </Link>
        <Link href="/dashboard" className="hidden md:inline-flex rounded px-3 py-1 text-sm font-medium hover:bg-gray-100">
          ダッシュボード
        </Link>
        <Link href="/courses" className="hidden md:inline-flex rounded px-3 py-1 text-sm font-medium hover:bg-gray-100">
          コース
        </Link>
        {showAddEditionLink && (
          <Link href="/editions" className="hidden md:inline-flex rounded px-3 py-1 text-sm font-medium text-blue-700 hover:bg-gray-100">
            カードタイプを追加する
          </Link>
        )}

        <div ref={menuRef} className="relative">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="rounded px-3 py-1 text-sm font-medium hover:bg-gray-100 cursor-pointer"
          >
            メニュー
          </button>

          {open && (
            <div className="absolute right-0 mt-2 w-52 rounded-md border bg-white shadow-lg z-50">
              <div className="py-1">
                {/* モバイルのみ: ピープル・ダッシュボード・コース */}
                <Link href="/people" className="block md:hidden px-4 py-2 text-sm hover:bg-gray-50" onClick={() => setOpen(false)}>
                  ピープル
                </Link>
                <Link href="/dashboard" className="block md:hidden px-4 py-2 text-sm hover:bg-gray-50" onClick={() => setOpen(false)}>
                  ダッシュボード
                </Link>
                <Link href="/courses" className="block md:hidden px-4 py-2 text-sm hover:bg-gray-50" onClick={() => setOpen(false)}>
                  コース
                </Link>
                {/* 2026-07-13・Kazuto指摘：サイドバー（カードタイプ切り替え・/editionsへの導線）は
                    モバイル幅で非表示のため、複数版を持つユーザーがモバイルで版を切り替える手段が
                    無かった。/editionsページ自体には既に版ごとの「アプリで使う」ボタンがあるので、
                    ここへの入口をメニューに追加するだけで解消する（モバイル限定で表示） */}
                <Link
                  href="/editions"
                  className="flex md:hidden items-center justify-between px-4 py-2 text-sm text-blue-700 font-medium hover:bg-gray-50"
                  onClick={() => setOpen(false)}
                >
                  カードタイプ切替
                  <span className="ml-2 rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">
                    New
                  </span>
                </Link>
                <div className="my-1 border-t md:hidden" />

                {/* 記録を見る */}
                <p className="px-4 pt-2 pb-1 text-xs text-gray-400 font-medium">記録を見る</p>
                <Link href="/sessions" className="block px-4 py-2 text-sm hover:bg-gray-50" onClick={() => setOpen(false)}>
                  自分の回答
                </Link>
                <Link href="/posts" className="block px-4 py-2 text-sm hover:bg-gray-50" onClick={() => setOpen(false)}>
                  みんなの回答
                </Link>
                <Link href="/discoveries" className="block px-4 py-2 text-sm hover:bg-gray-50" onClick={() => setOpen(false)}>
                  発見ノート
                </Link>
                <Link href="/practice" className="block px-4 py-2 text-sm hover:bg-gray-50" onClick={() => setOpen(false)}>
                  実践レポート
                </Link>
                <Link href="/notifications" className="block px-4 py-2 text-sm hover:bg-gray-50" onClick={() => setOpen(false)}>
                  通知
                </Link>

                <div className="my-1 border-t" />

                {/* 設定 */}
                <p className="px-4 pt-2 pb-1 text-xs text-gray-400 font-medium">設定</p>
                <Link href="/account?tab=basic" className="block px-4 py-2 text-sm hover:bg-gray-50" onClick={() => setOpen(false)}>
                  アカウント設定
                </Link>
                <Link href="/help/install" className="block px-4 py-2 text-sm hover:bg-gray-50" onClick={() => setOpen(false)}>
                  ホーム画面に追加
                </Link>

                <div className="my-1 border-t" />

                {/* 外部 */}
                <Link
                  href="https://www.kataruta.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-4 py-2 text-sm hover:bg-gray-50"
                  onClick={() => setOpen(false)}
                >
                  公式サイト
                </Link>
              </div>
            </div>
          )}
        </div>

        {specialAccess && pathname.startsWith("/courses") && (
          <span
            className="inline-flex items-center whitespace-nowrap rounded-full bg-pink-100 px-2.5 py-1 text-xs font-semibold text-pink-700"
            title="Shopify購入を経由しない、ワンオフの手動付与によるアクセスです"
          >
            特別アクセス中
          </span>
        )}
        <UserButton afterSignOutUrl={pathname} />
      </nav>
    </header>
  );
}
